import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api/client';
import { getAuth, saveAuth, getRefreshToken } from '../utils/auth';
import { dbgV, dbgI, dbgW, rkLog } from '../utils/debug';
import { withRetry } from '../utils/retryFetch';

const COMP = 'SubtenantPicker';

/**
 * A single switchable context (division) as returned by the IdP via
 * GET /api/contexts. Items have no tenant_slug of their own — they are
 * divisions WITHIN the caller's current tenant, so the switch call uses the
 * current_tenant_slug from the same response.
 */
interface ContextItem {
  id: string;
  display_name: string;
  parent_id: string | null;
  kind: string;
}

interface ContextsResponse {
  items: ContextItem[];
  can_switch: boolean;
  current_subtenant_id: string | null;
  current_tenant_slug: string | null;
}

interface SwitchResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * Division (subtenant) picker for the navbar.
 *
 * Behaviour (P4-4):
 *  - On mount, GET /api/contexts.
 *  - If can_switch is false / items.length <= 1: render nothing — EXCEPT when
 *    the JWT has no active subtenant yet AND exactly one division exists, in
 *    which case auto-switch into it on mount.
 *  - With multiple divisions: render a <select>. On change, POST the switch,
 *    persist the re-issued tokens, and dispatch `rk:contextswitch` so reactive
 *    components reload.
 *
 * Subtenant scope is not a PHI security boundary (PHI rides on capabilities),
 * so a failed contexts load fails quietly to "no picker" rather than blocking
 * the UI; switch failures surface an inline error.
 */
export default function SubtenantPicker() {
  const [items, setItems] = useState<ContextItem[]>([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [currentSubtenantId, setCurrentSubtenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  // True while the initial /api/contexts fetch (including any retries) is in flight.
  const [loadingContexts, setLoadingContexts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guard so the single-division auto-switch fires at most once per mount.
  const autoSwitchedRef = useRef(false);

  const auth = getAuth();
  const authToken = auth?.token ?? null;

  // Perform a context switch to the given subtenant within tenantSlug.
  const doSwitch = useCallback(async (slug: string, subtenantId: string): Promise<boolean> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      dbgW(COMP, 'doSwitch: no refresh token — cannot switch context');
      setError('Session expired — please sign in again.');
      return false;
    }
    setSwitching(true);
    setError(null);
    try {
      const data = await apiFetch<SwitchResponse>('/api/contexts/switch', {
        method: 'POST',
        body: JSON.stringify({
          tenant_slug: slug,
          subtenant_id: subtenantId,
          refresh_token: refreshToken,
        }),
      });
      const currentAuth = getAuth();
      if (!currentAuth) {
        dbgW(COMP, 'doSwitch: auth vanished mid-switch');
        return false;
      }
      const updatedUser = { ...currentAuth.user, subtenant_id: subtenantId };
      saveAuth(data.access_token, updatedUser, data.refresh_token);
      setCurrentSubtenantId(subtenantId);
      dbgI(COMP, 'doSwitch: context switched', { slug, subtenantId });
      // Notify reactive components (route data fetchers, dashboards) to reload
      // now that the active division scope changed.
      try {
        window.dispatchEvent(new Event('rk:contextswitch'));
      } catch {
        // environments without window don't need the event
      }
      return true;
    } catch (err) {
      const msg = String(err instanceof Error ? err.message : err);
      dbgW(COMP, 'doSwitch: switch call failed', { error: msg });
      // 403 (access denied) / 503 (context service unavailable) surface here.
      setError(
        msg.toLowerCase().includes('unavailable')
          ? 'Division service unavailable — try again shortly.'
          : 'Could not switch division.'
      );
      return false;
    } finally {
      setSwitching(false);
    }
  }, []);

  // Load contexts on mount (and whenever the access token changes — e.g. after
  // a tenant switch the available divisions differ).
  //
  // Retry policy: up to 2 retries (3 total attempts) with 400ms / 1000ms
  // backoff, for transient 503s only (IdP upstream latency spikes). Auth
  // errors (401/403) are NOT retried and surface immediately via the .catch
  // path. loadingContexts stays true across all attempts so we never flash an
  // error mid-retry.
  useEffect(() => {
    if (!authToken) {
      dbgV(COMP, 'contexts fetch skipped — no auth state');
      return;
    }
    let cancelled = false;
    setLoadingContexts(true);
    dbgI(COMP, 'fetching contexts from /api/contexts');
    withRetry(
      () => apiFetch<ContextsResponse>('/api/contexts', { skipLogoutOn401: true }),
      {
        maxRetries: 2,
        onRetry: (attempt, err) => {
          dbgW(COMP, `/api/contexts transient failure — retry ${attempt}`, { error: String(err) });
        },
      },
    )
      .then(res => {
        if (cancelled) return;
        setLoadingContexts(false);
        setItems(res.items ?? []);
        setCanSwitch(!!res.can_switch);
        setCurrentSubtenantId(res.current_subtenant_id ?? null);
        setTenantSlug(res.current_tenant_slug ?? null);

        // Auto-switch: if the user has no active division yet but exactly one
        // is available, adopt it transparently so downstream scoping is set.
        if (
          !autoSwitchedRef.current &&
          (res.current_subtenant_id == null) &&
          (res.items?.length === 1) &&
          res.current_tenant_slug
        ) {
          autoSwitchedRef.current = true;
          dbgI(COMP, 'auto-switching into sole division on mount', { id: res.items[0].id });
          void doSwitch(res.current_tenant_slug, res.items[0].id);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setLoadingContexts(false);
        // Fail quietly: no picker. Subtenant is not a PHI gate, so a contexts
        // outage must not block the navbar. All retries are exhausted by this
        // point; the error is only surfaced to the debug log.
        dbgW(COMP, '/api/contexts failed (all retries exhausted) — hiding picker', { error: String(err) });
        setItems([]);
        setCanSwitch(false);
      });
    return () => { cancelled = true; };
  }, [authToken, doSwitch]);

  // While the initial contexts fetch (including any retries) is still in
  // flight, render nothing — we don't yet know whether to show the picker.
  // This prevents a transient 503 + retry from flashing an empty state to the
  // user before the successful response arrives.
  if (loadingContexts) {
    return null;
  }

  // Render nothing unless there are multiple switchable divisions.
  if (!canSwitch || items.length <= 1) {
    return null;
  }

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <select
        value={currentSubtenantId ?? ''}
        disabled={switching || !tenantSlug}
        onChange={e => {
          const subtenantId = e.target.value;
          if (!subtenantId || !tenantSlug) return;
          rkLog('division switcher: option selected ->', subtenantId);
          void doSwitch(tenantSlug, subtenantId);
        }}
        style={{
          fontSize: 12, padding: '4px 8px',
          background: 'var(--surface-2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', color: 'var(--text)',
          cursor: switching ? 'wait' : 'pointer',
        }}
        title="Switch division"
      >
        {/* Placeholder when no division is active yet */}
        {currentSubtenantId == null && <option value="">Select division…</option>}
        {items.map(item => (
          <option key={item.id} value={item.id}>{item.display_name}</option>
        ))}
      </select>
      {error && (
        <span style={{ fontSize: 11, color: 'var(--danger)' }} role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
